/**
 * Utility function to emit a custom event with the specified name and detail data.
 * @param host - The event target to dispatch the event on
 * @param name - The name of the event to emit
 * @param detail - The detail data to include in the event
 */
export function emitEvent(host: EventTarget, name: string, detail: unknown): void {
    host.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
}

/**
 * Utility function to set form validity and emit a validity change event if the validity state changes
 * @param internals - The ElementInternals instance associated with the form element
 * @param emit - A function to emit events, typically the emitEvent function defined above
 * @param flags - An optional ValidityStateFlags object indicating the validity state to set; if not provided, validity will be set to valid
 * @param message - An optional validation message to associate with the validity state
 * @param anchor - An optional HTMLElement to anchor the validation message to; if not provided, the event will reference the host element
 * @param host - An optional HTMLElement representing the host element to reference in the event; if not provided, the anchor or internals will be used as the event target
 */
export function setDerValidity(
    internals: ElementInternals,
    emit: (name: string, detail: unknown) => void,
    flags?: ValidityStateFlags,
    message?: string,
    anchor?: HTMLElement,
    host?: HTMLElement
): void {
    const currentValid = internals.validity.valid;

    if (!flags || Object.values(flags).every((v) => !v)) {
        internals.setValidity({});
    } else {
        internals.setValidity(flags, message!, anchor);
    }

    const newValid = internals.validity.valid;

    if (currentValid !== newValid) {
        emit('der-validity-change', {
            validity: internals.validity,
            validationMessage: message,
            element: anchor || host,
        });
    }
}

/**
 * Utility function to emit a warning event with the specified name and data
 * @param host - The event target to dispatch the event on
 * @param name - The name of the warning event to emit
 * @param data - Additional data to include in the event detail
 */
export function emitWarning(host: EventTarget, name: string, data: object): void {
    const detail = { name: name.toLowerCase(), data };
    host.dispatchEvent(new CustomEvent('der.warning', { detail, bubbles: true, composed: true }));
}

/**
 * Utility function to emit an error event with the specified name and data
 * @param host - The event target to dispatch the event on
 * @param name - The name of the error event to emit
 * @param data - Additional data to include in the event detail
 */
export function emitError(host: EventTarget, name: string, data: object): void {
    const detail = { name: name.toLowerCase(), data };
    host.dispatchEvent(new CustomEvent('der.error', { detail, bubbles: true, composed: true }));
}
